import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { WhatsAppIcon } from "./Logo";
import { waLink } from "../lib/brand";

const GROUPS = [
  {
    key: "type",
    label: "I'm looking for",
    testid: "estimator-type",
    options: ["Plot", "Builder Floor", "Farmhouse", "Commercial Space", "Agricultural Land"],
  },
  {
    key: "purpose",
    label: "I want to",
    testid: "estimator-purpose",
    options: ["Buy", "Sell", "Rent", "Invest"],
  },
  {
    key: "budget",
    label: "My budget range",
    testid: "estimator-budget",
    options: ["Under ₹25 L", "₹25 L – ₹50 L", "₹50 L – ₹1 Cr", "₹1 Cr – ₹3 Cr", "₹3 Cr +"],
  },
];

export const Estimator = () => {
  const [sel, setSel] = useState({ type: "Plot", purpose: "Buy", budget: "₹50 L – ₹1 Cr" });

  const pick = (key, val) => setSel((s) => ({ ...s, [key]: val }));

  const message = `Hello Laxmi Jiwan Properties!%0A%0AI would like to ${sel.purpose.toLowerCase()} a ${sel.type} in Hisar.%0ABudget range: ${sel.budget}.%0A%0APlease share suitable options.`;
  const link = waLink(
    `Hello Laxmi Jiwan Properties!\n\nI would like to ${sel.purpose.toLowerCase()} a ${sel.type} in Hisar.\nBudget range: ${sel.budget}.\n\nPlease share suitable options.`
  );

  return (
    <section className="relative py-16 sm:py-24 lg:py-28" data-testid="estimator-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-[2rem] border border-[#e7e3d7] bg-[#ffffff] p-6 sm:p-8 lg:p-12 shadow-[0_30px_80px_-40px_rgba(19,19,17,0.35)]"
        >
          <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(197,160,89,0.14),transparent_65%)]" />

          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="h-5 w-5 text-[#c5a059]" />
            <span className="text-xs tracking-[0.34em] uppercase text-[#9a7b38] font-semibold">
              Tell Us What You Need
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#131311] leading-[1.02]">
            Build your enquiry in seconds.
          </h2>
          <p className="mt-3 text-[#52514c] max-w-xl">
            Choose a few details and we'll open WhatsApp with your message ready to
            send. No forms, no waiting.
          </p>

          <div className="mt-10 space-y-8">
            {GROUPS.map((g) => (
              <div key={g.key} data-testid={g.testid}>
                <div className="text-sm font-semibold text-[#131311] mb-3">
                  {g.label}
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {g.options.map((opt) => {
                    const active = sel[g.key] === opt;
                    return (
                      <button
                        key={opt}
                        onClick={() => pick(g.key, opt)}
                        className={`rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300 ${
                          active
                            ? "bg-[#131311] text-[#faf9f5] shadow-[0_8px_20px_-8px_rgba(19,19,17,0.6)]"
                            : "border border-[#e0dccf] text-[#52514c] hover:border-[#c5a059] hover:text-[#131311]"
                        }`}
                        data-testid={`${g.testid}-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-10 inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25d366] text-white px-8 py-4 text-sm font-semibold shadow-[0_14px_34px_rgba(37,211,102,0.3)] hover:bg-[#1eae53] transition-all duration-300 hover:-translate-y-0.5"
            data-testid="estimator-submit-whatsapp"
          >
            <WhatsAppIcon className="h-5 w-5" />
            Send my enquiry on WhatsApp
          </a>
        </motion.div>
      </div>
    </section>
  );
};
